import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';

/**
 * Suppress the benign "ResizeObserver loop" warnings.
 *
 * These errors are not actual application failures — they occur when a
 * ResizeObserver callback adjusts element sizes within the same frame
 * (e.g. MUI Skeleton, Grid, Dialog, or lazy-loaded images). The browser
 * reports them as uncaught errors, and React 18 re-surfaces them through
 * its internal error handler / console, which the `window` error event
 * alone does not intercept.
 *
 * We filter these out from every reporting channel (window error event,
 * window.onerror, and console.error) in development to prevent them from
 * cluttering the console and triggering false error overlays.
 */
const isBenignResizeObserverError = (message?: string | Event): boolean => {
  if (typeof message === 'string') {
    return (
      message.includes('ResizeObserver loop completed with undelivered notifications') ||
      message.includes('ResizeObserver loop limit exceeded')
    );
  }
  return false;
};

if (process.env.NODE_ENV === 'development') {
  // Use capture phase so our handler runs BEFORE CRA's react-error-overlay
  // listener (which is registered before this module executes). This lets us
  // stop the error from reaching the overlay.
  window.addEventListener(
    'error',
    (e) => {
      if (isBenignResizeObserverError(e.message)) {
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    },
    true
  );

  // Patch console.error to suppress React 18's error overlay, which reports
  // these errors through `handleError` -> console.error (see stack trace).
  const originalConsoleError = console.error;
  console.error = (...args: unknown[]) => {
    const message = args[0];
    if (typeof message === 'string' && isBenignResizeObserverError(message)) {
      return;
    }
    // Some libraries pass an Error object as the first arg
    if (message instanceof Error && isBenignResizeObserverError(message.message)) {
      return;
    }
    originalConsoleError.apply(console, args);
  };
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);